<template>
	<div class="title-timeline" :class="{ 'title-timeline--one-bucket': !isPremium }">
		<div class="timeline">
			<div
				v-for="(bucket, bucketIndex) in formattedTimelineData"
				:key="bucket.key"
				class="timeline__timeframe"
				:class="{ ['timeline__timeframe--' + bucket.key]: true }"
			>
				<TimelineHeader v-if="bucket.key">
					<TimeAgo v-if="bucket.isGroupedByDate" :date="bucket.key" />
					<span v-else>
						{{ getBucketTranslatable(bucket.key) }}
					</span>
				</TimelineHeader>

				<!-- CLS issue: when filtering or changing providers, title rows are re-used and shifted up or down the DOM causing CLS. -->
				<!-- By tying the key to their index down the page we ensure that Vue re-creates the component instead of shifting it in the DOM. -->
				<div
					class="timeline__provider-block"
					:class="{
						['timeline__timeframe--' + bucket.key + '--' + provider.key.package.shortName]: true,
					}"
					v-for="(provider, providerIndex) in bucket.providers"
					:key="`${provider.key.package.packageId}-${providerIndex}`"
				>
					<ProviderTimeline
						:titleCount="provider.node.totalCount"
						:providerId="provider.key.package.packageId"
						:providerIcon="provider.key.package.icon"
						:providerShortName="provider.key.package.shortName"
						:dayIndex="bucketIndex"
						:providerIndex="providerIndex"
						:timelineDate="getBucketDate(provider)"
						:bucketType="getBucketType(bucket)"
						@onPosterClick="$emit('onPosterClick', $event)"
						@passUpTitles="allTitles = [...allTitles, $event]"
					/>

					<TimelineAdUnit
						v-if="!isLeavingSoonOrComingSoon"
						:index="getIndexByProvider(bucketIndex, providerIndex)"
					/>
				</div>
			</div>

			<InfiniteScroll
				:disabled="infiniteScrollDisabled"
				:loading="isLoading"
				@load-next="$emit('load-next', $event)"
				ref="infiniteScroll"
			/>
		</div>

		<TimelineEmptyState
			v-if="showEmptyTimeline"
			:hasNoTitles="isFetchedListEmpty"
			:isAtEndOfList="isAtEndOfList"
			@onScrollToTop="$emit('onScrollToTop')"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import TimeAgo from '@/components/TimeAgo.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import ProviderTimeline from '@/components/timeline/ProviderTimeline.vue';
import TimelineAdUnit from '@/components/timeline/TimelineAdUnit.vue';
import TimelineHeader from '@/components/timeline/TimelineHeader.vue';
import { BucketData, FormattedTimelineData } from '@/interfaces/new-title-graphql';
import { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import { NewDateRangeBucket, NewPageType, NewTitlesEdge } from '@/@types/graphql-types';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
const TimelineEmptyState = defineAsyncComponent(
	() => import(/* webpackChunkName: "timeline-empty-state" */ '@/components/timeline/TimelineEmptyState.vue')
);

const props = defineProps<{
	formattedTimelineData: FormattedTimelineData[];
	newPageType: NewPageType;
	isLoading?: boolean;
	isFirstPage?: boolean;
	isAtEndOfList?: boolean;
}>();

const emit = defineEmits<{
	(e: 'onScrollToTop'): void;
	(e: 'onPosterClick', payload: TitleEventPayloadGraphql): void;
	(e: 'mounted'): void;
	(e: 'passUpTitles', titles: NewTitlesEdge[]): void;
	(e: 'load-next', payload: { event: any }): void;
}>();

const { isPremium } = storeToRefs(useUserStore());
const { t } = useI18n();

const allTitles = ref<NewTitlesEdge[]>([]);
const allNewTitles = computed(() => allTitles.value.filter(Boolean).flat());
const isFetchedListEmpty = computed(() => props.formattedTimelineData.length === 0);
const showEmptyStateContent = computed(() => !props.isLoading && isFetchedListEmpty.value);
const isLeavingSoonOrComingSoon = computed(() =>
	[NewPageType.LeavingSoon, NewPageType.Upcoming].includes(props.newPageType)
);
const infiniteScrollDisabled = computed(() => isFetchedListEmpty.value || props.isAtEndOfList);
const infiniteScroll = ref<InstanceType<typeof InfiniteScroll> | null>(null);

const showEmptyTimeline = computed(() => {
	if (props.isLoading) return false;

	const showEmptyTimeline = showEmptyStateContent.value || props.isAtEndOfList;

	if (isLeavingSoonOrComingSoon.value) {
		return showEmptyTimeline && isPremium.value;
	}
	return showEmptyTimeline;
});

function getIndexByProvider(dayIndex: number, providerIndex: number) {
	if (dayIndex > 0) {
		[...Array(dayIndex).keys()].forEach(i => {
			const item = props.formattedTimelineData[i + 1];
			if (item) {
				providerIndex += item.providers.length;
			}
		});
	}
	return providerIndex;
}

function getBucketDate(bucket: BucketData) {
	return bucket.key?.date ?? '';
}

function getBucketType(bucket: FormattedTimelineData) {
	return !bucket.isGroupedByDate ? (bucket.key as NewDateRangeBucket) : null;
}

function getBucketTranslatable(bucketKey: string) {
	const translationMap: Record<NewDateRangeBucket, string> = {
		[NewDateRangeBucket.Today]: 'WEBAPP_TODAY',
		[NewDateRangeBucket.Tomorrow]: 'WEBAPP_DATE_TOMORROW',
		[NewDateRangeBucket.Next_7Days]: 'WEBAPP_BADGE_IN_DAYS',
		[NewDateRangeBucket.Next_30Days]: 'WEBAPP_BADGE_IN_DAYS',
	};

	const days = bucketKey === NewDateRangeBucket.Next_7Days ? 7 : 30;

	return t(translationMap[bucketKey as NewDateRangeBucket], { days });
}
// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
function resetInfiniteScroll() {
	infiniteScroll.value?.reset();
}

watch(allNewTitles, () => {
	emit('passUpTitles', allNewTitles.value);
});

useProvideClickoutPlacement('title_timeline');

onMounted(() => {
	emit('mounted');
});

defineExpose({
	resetInfiniteScroll,
});
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';

.title-timeline {
	min-height: 80vh;
	padding-top: 30px;

	&--one-bucket {
		min-height: auto;
	}
}

.timeline {
	$r: &;

	position: relative;
	opacity: 1;
	transition: opacity 0.35s ease-in-out;
	height: 100%;

	$logo-size: 25px;
	$timeline-padding: 10px;
	$timeline-line-width: 2px;

	// line in the timeline
	border-inline-start: $timeline-line-width solid $jw-color-darkgray;
	padding-inline-start: $timeline-padding;
	margin-inline-start: calc($grid-gutter-width / 2) - $timeline-line-width;
	text-align: start;

	&--loading {
		opacity: 0.5;
	}

	// bullet point on timeline
	&__timeframe {
		padding-bottom: 5px;

		&:before {
			$size: 10px;
			position: absolute;
			content: '';
			margin-top: -10px;
			margin-inline-start: -$timeline-padding - calc(($size / 2) - 1px);
			width: $size;
			height: $size;
			border-radius: $size * 2;
			background: var(--jw-color-secondary);
		}
	}

	&__provider-block {
		margin-bottom: 25px;
		margin-inline-start: 10px;
		// @TODO the horizontal title list without the price drop display only
		// requires 260px height
		// height: 320px;
	}
}
</style>
